import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    TextField,
    Checkbox,
    Autocomplete,
    IconButton,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';

/**
 * EditableAssociationRow Component
 * Renders a single row in the EditableAssociation component for one assignment.
 * Allows selecting the associated record and editing custom fields.
 * @param {Object} assignment - The current assignment object with _key and properties
 * @param {Array} fields - Configuration for additional fields to render
 * @param {Array} allRecords - Full list of available records for selection
 * @param {Boolean} loadingRecords - Whether the available records are still loading
 * @param {Function} getFilteredOptions - Function to get filtered options for the record selector
 * @param {String} idField - Field name used as unique identifier
 * @param {Function} onUpdate - Callback when this assignment is updated
 * @param {Function} onRemove - Callback to remove this assignment
 * @param {Function} getOptionLabel - Function to get display label for options
 */
const EditableAssociationRow = ({
    assignment,
    fields,
    allRecords,
    loadingRecords,
    getFilteredOptions,
    idField,
    dataField,
    onUpdate,
    onRemove,
    getOptionLabel,
}) => {
    // Find the currently selected record object for this assignment
    const selectedRecord = useMemo(() => {
        const assignmentValue = assignment && typeof assignment === 'object' ? assignment[idField] : assignment;
        if (assignmentValue == null) return null;
        return allRecords.find(r => String(r?.[idField]) === String(assignmentValue)) || null;
    }, [assignment, idField, allRecords]);

    // Compute options specific to this row (filtering out already selected items)
    const options = useMemo(() => {
        return getFilteredOptions(assignment._key);
    }, [getFilteredOptions, assignment._key]);

    const handleFieldChange = (fieldName, value) => {
        onUpdate({ [fieldName]: value });
    };

    const handleRecordSelect = (event, newValue) => {
        onUpdate({ [idField]: newValue?.[idField], [dataField]: newValue?.[dataField] || null });
    };

    const getFieldValue = (field) => {
        return assignment[field.name] !== undefined ? assignment[field.name] : (field.type === 'checkbox' ? false : '');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', md: 'center' },
            }}
        >
            {/* Primary record selector */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 280, maxWidth: { md: 350 } }}
                options={options}
                value={selectedRecord}
                onChange={handleRecordSelect}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(option, value) => String(option?.[idField]) === String(value?.[idField])}
                loading={loadingRecords}
                renderInput={(params) => (
                    <TextField {...params} placeholder="Search or select..." />
                )}
            />

            {/* Additional fields based on config */}
            {fields.map(field => {
                const value = getFieldValue(field);

                if (field.type === 'checkbox') {
                    return (
                        <Checkbox
                            key={field.name}
                            checked={value || false}
                            onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                            disableRipple
                            sx={{
                                width: field.width,
                                color: 'secondary.main',
                                '&.Mui-checked': {
                                    color: 'secondary.main',
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: 'transparent',
                                },
                                '&.Mui-checked:hover': {
                                    color: 'primary.main',
                                },
                            }}
                        />
                    );
                }

                if (field.type === 'text') {
                    return (
                        <TextField
                            key={field.name}
                            value={value || ''}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            size="small"
                            sx={{ width: field.width, flex: field.flex, minWidth: 200 }}
                            placeholder={field.placeholder}
                            multiline={field.multiline}
                            minRows={field.minRows || 1}
                            maxRows={field.maxRows || (field.multiline ? 3 : 1)}
                        />
                    );
                }

                if (field.type === 'select') {
                    return (
                        <FormControl key={field.name} size="small" sx={{ width: field.width, minWidth: 120 }}>
                            <Select
                                value={value || ''}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    <em>Select...</em>
                                </MenuItem>
                                {(field.options || []).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                return null;
            })}

            {/* Delete button */}
            <IconButton
                onClick={onRemove}
                color="error"
                size="small"
                title="Remove record"
            >
                <RemoveIcon />
            </IconButton>
        </Box>
    );
};

EditableAssociationRow.propTypes = {
    assignment: PropTypes.object.isRequired,
    fields: PropTypes.array.isRequired,
    allRecords: PropTypes.array.isRequired,
    loadingRecords: PropTypes.bool,
    getFilteredOptions: PropTypes.func.isRequired,
    idField: PropTypes.string.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    getOptionLabel: PropTypes.func.isRequired,
};

export default EditableAssociationRow;
